/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ReactNode } from 'react'
import { createElement } from 'react'

import { extractContentIdFromUrl, shouldAddTracking } from '../utils/get-tracking-info'
import { componentMap } from './component-map'

const FREE_ACCOUNT_LOCK_ELEMENT_THRESHOLD = 3

type AdditionalInfo = {
  articleUrl?: string
  subVerticalSlug?: string
  context?: string
  title?: string
  showFreeAccountLock?: boolean
  userLoggedIn?: boolean
}

type RenderState = {
  topLevelCount: number
  isTopLevel: boolean
}

export const renderArticleElements = (
  articleBody: any = [],
  additionalInfo?: AdditionalInfo
): ReactNode[] => {
  return renderElements(articleBody, additionalInfo, { topLevelCount: 0, isTopLevel: true })
}

const renderElements = (
  articleBody: any[],
  additionalInfo: AdditionalInfo | undefined,
  state: RenderState
): ReactNode[] => {
  return articleBody.reduce((elements: ReactNode[], currentElement: any) => {
    if (!currentElement?.contentTag && !currentElement?.tagName && !currentElement?.component) {
      if (!skipableElements.includes(currentElement?.name ?? '')) {
        console.error('ARTICLE RENDERER ERROR: Invalid article body json element:', {
          articleUrl: additionalInfo?.articleUrl ?? 'unknown',
          invalidElement: currentElement,
        })
      }

      return elements
    }

    if (
      additionalInfo?.showFreeAccountLock &&
      state.isTopLevel &&
      state.topLevelCount >= FREE_ACCOUNT_LOCK_ELEMENT_THRESHOLD
    ) {
      return elements
    }

    if (currentElement.contentTag) {
      return renderContentTagElement({ elements, currentElement, additionalInfo, state })
    }

    if (currentElement.tagName === 'text') {
      return elements.concat(currentElement.innerText)
    }

    let renderedElement: ReactNode

    if (currentElement.component) {
      const component = componentMap[currentElement.component as keyof typeof componentMap]
      const componentRequiresRenderer = componentsWithRenderers.includes(currentElement.component)

      const combinedInnerText = currentElement.children
        ? currentElement.children.map((child: any) => child.innerText).join('')
        : ''

      const rendererProp = componentRequiresRenderer
        ? {
            renderArticleElements: (body: any) => renderElements(body, additionalInfo, state),
          }
        : {}

      if (
        currentElement.component === 'StandardLink' &&
        shouldAddTracking(additionalInfo?.subVerticalSlug) &&
        additionalInfo?.context === 'accordion' &&
        additionalInfo?.articleUrl?.includes(`/topic/`)
      ) {
        const trackingProps = {
          'data-which-id': `${additionalInfo.subVerticalSlug} link`,
          'data-section': additionalInfo.title,
          'data-card-name': combinedInnerText,
          'data-content-id': extractContentIdFromUrl(currentElement.props.href),
        }

        renderedElement = createElement(
          (component as any) || 'a',
          component
            ? {
                key: currentElement.props?.key || currentElement.id,
                ...currentElement.props,
                ...trackingProps,
                ...rendererProp,
              }
            : { key: currentElement.props?.key || currentElement.id },
          calculateElementContent({ element: currentElement, additionalInfo, state })
        )
      } else {
        renderedElement = createElement(
          (component as any) || 'div',
          component
            ? {
                key: currentElement.props?.key || currentElement.id,
                ...currentElement.props,
                ...rendererProp,
              }
            : { key: currentElement.props?.key || currentElement.id },
          calculateElementContent({ element: currentElement, additionalInfo, state })
        )
      }
    } else {
      renderedElement = createElement(
        currentElement.tagName,
        { ...currentElement.props, key: currentElement.props?.key || currentElement.id },
        calculateElementContent({ element: currentElement, additionalInfo, state })
      )
    }

    if (state.isTopLevel) {
      state.topLevelCount++
    }

    return elements.concat(renderedElement)
  }, [])
}

const calculateElementContent = ({
  element,
  additionalInfo,
  state,
}: {
  element: any
  additionalInfo: AdditionalInfo | undefined
  state: RenderState
}): ReactNode => {
  const { innerText, children } = element

  if (innerText) {
    return innerText
  }

  if (children) {
    return renderElements(children, additionalInfo, state)
  }

  return null
}

const componentsWithRenderers = ['CsvTable']

const skipableElements = ['logged-in-content', 'logged-out-content']

const renderContentTagElement = ({
  elements,
  currentElement,
  additionalInfo,
  state,
}: {
  elements: ReactNode[]
  currentElement: any
  additionalInfo: AdditionalInfo | undefined
  state: RenderState
}): ReactNode[] => {
  const wasTopLevel = state.isTopLevel
  state.isTopLevel = false
  const result = elements.concat(renderElements(currentElement.children, additionalInfo, state))
  state.isTopLevel = wasTopLevel
  if (wasTopLevel) {
    state.topLevelCount++
  }
  return result
}
